import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useMutation, useQuery } from "convex/react";
import { Link, useLocation } from "react-router";
import { GripVertical, SlidersHorizontal } from "lucide-react";
import { Reorder, useReducedMotion } from "motion/react";

import { AppIcon } from "../../components/apps/AppIcon";
import {
  TAB_BODY_COMPACT_TIMEFRAME_CLASS,
  TabPageHeader,
  TabPageTimeframe,
} from "../../components/navigation/TabPageHeader";
import { TabPageShell } from "../../components/navigation/TabPageShell";
import { assignBrandAppColors } from "../../lib/apps/appUsageColors";
import { resolveCategoryColor } from "../../lib/apps/categoryColors";
import { AppFriendlyName } from "../../lib/apps/AppFriendlyName";
import { mutationRef, queryRef } from "../../lib/convex/refs";
import { useDeviceId } from "../../lib/device/DeviceProvider";
import { useMarketingDemoOverlay } from "../../lib/marketing/MarketingDemoProvider";
import { triggerReorderEnterHaptic, triggerSelectionChangeHaptic } from "../../lib/native/haptics";
import { formatDuration, todayKey } from "../../lib/format";
import {
  aggregateEntities,
  buildWeekGrid,
  intensityScale,
  resolveVisibleEntities,
  type HeatmapEntity,
  type HeatmapResult,
  type Metric,
  type ViewMode,
} from "../../lib/habits/heatmap";
import {
  isUnattributedHeatmapEntity,
  stripUnattributedFromKeys,
  topHabitsAppEntities,
} from "../../lib/habits/unattributed";
import { localizedCategoryLabel } from "../../lib/apps/categoryIcons";
import { resolveCanonicalCategoryKey } from "../../lib/apps/categoryNormalization";
import { AppDetailDrawer } from "./AppDetailDrawer";
import { AppFilterSheet, type FilterEntity } from "./AppFilterSheet";
import { HabitsHeaderToggles } from "./HabitsHeaderToggles";
import { HeatmapMatrix } from "./HeatmapMatrix";
import { useScreen0Language } from "@/features/language";
import { getTranslations } from "@/i18n";

const DEFAULT_COLOR = "#7ab89a";
const HABITS_APP_ICON_SIZE = 21;
const HABITS_APP_ICON_RENDER_SIZE = 27;
const DAY_LABEL_WIDTH = 22; // estimated width of day-label column at fontSize 9
const CARD_PADDING_X = 24; // px-3 = 12px × 2 sides
const LABEL_SCROLL_GAP = 6; // gap-1.5 between label column and scroll area

const WOBBLE_CSS = `
@keyframes card-wobble {
  0%   { transform: rotate(-0.9deg); }
  50%  { transform: rotate(0.9deg); }
  100% { transform: rotate(-0.9deg); }
}
.wobble-card {
  animation: card-wobble 0.55s ease-in-out infinite;
  transform-origin: center center;
}
`;

type Prefs = {
  trackedAppKeys: string[];
  viewMode: ViewMode;
  metric: Metric;
  appOrder?: string[];
  includeOther?: boolean;
};

type PrefsPatch = {
  trackedAppKeys?: string[];
  viewMode?: ViewMode;
  metric?: Metric;
  appOrder?: string[];
  includeOther?: boolean;
};

const heatmapRef = queryRef<
  { anonymousDeviceId: string; startDate: string; endDate: string },
  HeatmapResult
>("usageHeatmap:getHeatmap");
const prefsRef = queryRef<{ anonymousDeviceId: string }, Prefs | null>("habitPrefs:getPrefs");
const brandColorsRef = queryRef<Record<string, never>, Record<string, string>>(
  "appBrandColors:getMap",
);
const upsertPrefsRef = mutationRef<{ anonymousDeviceId: string; patch: PrefsPatch }, null>(
  "habitPrefs:upsertPrefs",
);

type ColoredEntity = {
  entity: HeatmapEntity;
  color: string;
};

export function HabitsTab() {
  const prefersReducedMotion = useReducedMotion();
  const { languageCode } = useScreen0Language();
  const t = getTranslations(languageCode);
  const anonymousDeviceId = useDeviceId();
  const referenceDate = todayKey();
  const grid = useMemo(() => buildWeekGrid(referenceDate), [referenceDate]);

  const heatmapReal = useQuery(heatmapRef, {
    anonymousDeviceId,
    startDate: grid.startDate,
    endDate: grid.endDate,
  });
  const heatmap = useMarketingDemoOverlay(
    "usageHeatmap:getHeatmap",
    { anonymousDeviceId, startDate: grid.startDate, endDate: grid.endDate },
    heatmapReal,
  );
  const prefs = useQuery(prefsRef, { anonymousDeviceId });
  const brandColorMap = useQuery(brandColorsRef, {});
  const upsertPrefs = useMutation(upsertPrefsRef);

  const [viewMode, setViewMode] = useState<ViewMode>("app");
  const [metric, setMetric] = useState<Metric>("time");
  const [tracked, setTracked] = useState<string[]>([]);
  const [appOrder, setAppOrder] = useState<string[]>([]);
  const [includeOther, setIncludeOther] = useState(false);
  const hydrated = useRef(false);

  const [reorderMode, setReorderMode] = useState(false);
  const [reorderEntities, setReorderEntities] = useState<ColoredEntity[]>([]);

  const mainRef = useRef<HTMLElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const location = useLocation();
  const isTabActive = location.pathname === "/habits";
  const [scrollResetKey, setScrollResetKey] = useState(0);

  useEffect(() => {
    const node = mainRef.current;
    if (!node) return;
    const ro = new ResizeObserver(([entry]) => {
      if (!isTabActive) return;
      setContainerWidth(entry.contentRect.width);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, [isTabActive]);

  // Remeasure when the keep-alive panel becomes visible and reset matrix scroll.
  useLayoutEffect(() => {
    if (!isTabActive) return;
    const node = mainRef.current;
    if (node) {
      const width = node.getBoundingClientRect().width;
      if (width > 0) setContainerWidth(width);
    }
    setScrollResetKey((key) => key + 1);
  }, [isTabActive]);

  useLayoutEffect(() => {
    if (prefs === undefined || hydrated.current) return;
    hydrated.current = true;
    if (prefs) {
      setViewMode(prefs.viewMode);
      setMetric(prefs.metric);
      setTracked(prefs.trackedAppKeys);
      if (prefs.appOrder?.length) setAppOrder(prefs.appOrder);
      setIncludeOther(prefs.includeOther ?? false);
    }
  }, [prefs]);

  const persist = useCallback(
    (patch: PrefsPatch) => {
      void upsertPrefs({ anonymousDeviceId, patch });
    },
    [anonymousDeviceId, upsertPrefs],
  );

  const onViewModeChange = (next: ViewMode) => {
    if (next === viewMode) return;
    triggerSelectionChangeHaptic();
    setViewMode(next);
    persist({ viewMode: next });
  };
  const onMetricChange = (next: Metric) => {
    if (next === metric) return;
    triggerSelectionChangeHaptic();
    setMetric(next);
    persist({ metric: next });
  };
  const onMetricToggle = () => {
    onMetricChange(metric === "time" ? "opens" : "time");
  };
  const onTrackedChange = (next: string[]) => {
    setTracked(next);
    persist({ trackedAppKeys: next });
  };
  const onIncludeOtherChange = (next: boolean) => {
    setIncludeOther(next);
    const patch: PrefsPatch = { includeOther: next };
    if (!next) {
      const cleanedTracked = stripUnattributedFromKeys(tracked);
      const cleanedOrder = stripUnattributedFromKeys(appOrder);
      setTracked(cleanedTracked);
      setAppOrder(cleanedOrder);
      patch.trackedAppKeys = cleanedTracked;
      patch.appOrder = cleanedOrder;
    }
    persist(patch);
  };

  const [filterOpen, setFilterOpen] = useState(false);
  const [detail, setDetail] = useState<{ entity: HeatmapEntity; color: string } | null>(null);
  const [detailDate, setDetailDate] = useState(referenceDate);

  const allEntities = useMemo(
    () =>
      heatmap ? aggregateEntities(heatmap.days, viewMode, metric, referenceDate) : [],
    [heatmap, viewMode, metric, referenceDate],
  );

  const topEntities = useMemo(
    () => topHabitsAppEntities(allEntities, viewMode, includeOther),
    [allEntities, viewMode, includeOther],
  );

  const visibleEntityList = useMemo(
    () => resolveVisibleEntities(allEntities, viewMode, tracked, includeOther),
    [allEntities, viewMode, tracked, includeOther],
  );

  const coloredEntities = useMemo<ColoredEntity[]>(() => {
    if (viewMode === "category") {
      return visibleEntityList.map((entity) => ({
        entity,
        color: resolveCategoryColor(entity.displayName),
      }));
    }
    const inputs = visibleEntityList.map((entity) => ({
      appIdentifier: entity.appIdentifier,
      displayName: entity.displayName,
      category: entity.category,
      color: undefined as string | undefined,
    }));
    const colored = assignBrandAppColors(inputs, brandColorMap, inputs.length);
    return visibleEntityList.map((entity, index) => ({
      entity,
      color: colored[index]?.color ?? DEFAULT_COLOR,
    }));
  }, [visibleEntityList, brandColorMap, viewMode]);

  const prefix = viewMode === "app" ? "app:" : "cat:";
  const selectedForMode = useMemo(
    () => tracked.filter((key) => key.startsWith(prefix)),
    [tracked, prefix],
  );

  const visibleEntities = useMemo(() => {
    if (selectedForMode.length === 0) return coloredEntities;
    const set = new Set(selectedForMode);
    return coloredEntities.filter((item) => set.has(item.entity.key));
  }, [coloredEntities, selectedForMode]);

  // Apply saved app order, filtering stale keys and appending new ones at the end.
  const displayedEntities = useMemo(() => {
    if (appOrder.length === 0) return visibleEntities;
    const keyToEntity = new Map(visibleEntities.map((e) => [e.entity.key, e]));
    const orderSet = new Set(appOrder);
    return [
      ...appOrder.filter((k) => keyToEntity.has(k)).map((k) => keyToEntity.get(k)!),
      ...visibleEntities.filter((e) => !orderSet.has(e.entity.key)),
    ];
  }, [visibleEntities, appOrder]);

  const filterEntities: FilterEntity[] = useMemo(() => {
    if (viewMode === "category") {
      return topEntities.map((entity) => ({
        key: entity.key,
        name: entity.displayName,
        displayName: entity.displayName,
        appIdentifier: entity.appIdentifier,
        category: entity.category,
        isCategory: entity.isCategory,
        color: resolveCategoryColor(entity.displayName),
      }));
    }
    const inputs = topEntities.map((entity) => ({
      appIdentifier: entity.appIdentifier,
      displayName: entity.displayName,
      category: entity.category,
      color: undefined as string | undefined,
    }));
    const colored = assignBrandAppColors(inputs, brandColorMap, inputs.length);
    return topEntities.map((entity, index) => ({
      key: entity.key,
      name: entity.displayName,
      displayName: entity.displayName,
      appIdentifier: entity.appIdentifier,
      category: entity.category,
      isCategory: entity.isCategory,
      color: colored[index]?.color ?? DEFAULT_COLOR,
    }));
  }, [topEntities, brandColorMap, viewMode]);

  // Dynamic cellSize — fills available card width, clamped to [9, 14].
  const cellSize = useMemo(() => {
    if (containerWidth === 0) return 11;
    const availableWidth = containerWidth - CARD_PADDING_X - DAY_LABEL_WIDTH - LABEL_SCROLL_GAP;
    const cols = grid.columns.length;
    const size = Math.floor((availableWidth - (cols - 1) * 2) / cols);
    return Math.max(9, Math.min(14, size));
  }, [containerWidth, grid.columns.length]);

  const prefsReady = prefs !== undefined;
  const isLoading = heatmap === undefined;
  const contentReady = prefsReady && !isLoading;
  const layoutReady = containerWidth > 0;
  const cardsReady = contentReady && layoutReady;
  const activeFilterCount = selectedForMode.length;

  const openDetail = useCallback((entity: HeatmapEntity, color: string, date: string) => {
    setDetail({ entity, color });
    setDetailDate(date);
  }, []);

  const toggleReorder = () => {
    if (!reorderMode) {
      triggerReorderEnterHaptic();
      setReorderEntities(displayedEntities);
      setReorderMode(true);
    } else {
      const newOrder = reorderEntities.map((e) => e.entity.key);
      setAppOrder(newOrder);
      persist({ appOrder: newOrder });
      setReorderMode(false);
    }
  };

  return (
    <>
      <style>{WOBBLE_CSS}</style>
      <TabPageShell
        bodyRef={mainRef}
        className={TAB_BODY_COMPACT_TIMEFRAME_CLASS}
        fixed={
          <TabPageHeader
            title={t.habits.title}
            actions={
              <div className="flex flex-wrap items-center justify-end gap-1.5">
                <HabitsHeaderToggles metric={metric} onMetricToggle={onMetricToggle} />
                <button
                  type="button"
                  onClick={toggleReorder}
                  className={`icon-button shrink-0${reorderMode ? " text-[oklch(72%_0.09_155)]" : ""}`}
                  aria-label={reorderMode ? t.habits.doneReordering : t.habits.reorder}
                >
                  <GripVertical size={20} />
                </button>
                <button
                  type="button"
                  onClick={() => setFilterOpen(true)}
                  className="icon-button relative shrink-0"
                  aria-label={t.habits.openFilter}
                >
                  <SlidersHorizontal size={20} />
                  {activeFilterCount > 0 ? (
                    <span className="absolute -right-1 -top-1 grid min-w-5 place-items-center rounded-full bg-[oklch(72%_0.09_155)] px-1 text-[10px] font-bold text-[oklch(12%_0.015_160)]">
                      {activeFilterCount}
                    </span>
                  ) : null}
                </button>
              </div>
            }
          />
        }
      >
        <TabPageTimeframe>{t.habits.timeframe}</TabPageTimeframe>

        {!cardsReady ? (
          <div className="flex min-w-0 flex-col gap-3">
            {Array.from({ length: 4 }, (_, index) => (
              <PlaceholderCard key={index} columns={grid.columns} cellSize={cellSize} />
            ))}
          </div>
        ) : null}

        {cardsReady && allEntities.length === 0 ? (
          <div className="liquid-glass-panel flex flex-col items-center justify-center gap-3 p-5 text-center">
            <p className="text-lg font-semibold text-[oklch(90%_0.03_155)]">{t.habits.emptyTitle}</p>
            <p className="max-w-xs text-sm text-[oklch(58%_0.025_155)]">
              {Capacitor.getPlatform() === "android" ? t.habits.emptyBodyAndroid : t.habits.emptyBody}
            </p>
            <Link to="/today" className="primary-action mt-2 w-full max-w-[220px]">
              {t.common.backToToday}
            </Link>
          </div>
        ) : null}

        {cardsReady && allEntities.length > 0 ? (
          reorderMode ? (
            <Reorder.Group
              as="div"
              axis="y"
              values={reorderEntities}
              onReorder={setReorderEntities}
              className="flex min-w-0 flex-col gap-3"
            >
              {reorderEntities.map((coloredEntity, index) => {
                const { entity, color } = coloredEntity;
                const values = grid.allDates.map((date) => entity.byDate.get(date) ?? 0);
                const scale = intensityScale(values);
                const windowTotalLabel =
                  entity.total > 0
                    ? metric === "time"
                      ? formatDuration(entity.total, languageCode)
                      : `${entity.total}`
                    : t.common.emDash;

                return (
                  <Reorder.Item
                    as="div"
                    key={entity.key}
                    value={coloredEntity}
                    style={{ touchAction: "pan-y" }}
                  >
                    <div
                      className={`min-w-0 overflow-hidden rounded-xl bg-[oklch(16%_0.015_165)] px-3 py-4${prefersReducedMotion ? "" : " wobble-card"}`}
                      style={{ animationDelay: `${index * 60}ms`, cursor: "grab", touchAction: "pan-y" }}
                    >
                      <div className="mb-3 flex items-center justify-between gap-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <AppIcon
                            displayName={entity.displayName}
                            appIdentifier={entity.appIdentifier}
                            category={entity.category}
                            color={color}
                            size={HABITS_APP_ICON_SIZE}
                            iconSize={HABITS_APP_ICON_RENDER_SIZE}
                            isCategory={entity.isCategory}
                          />
                          <HabitEntityLabel entity={entity} />
                        </div>
                        <span className="shrink-0 text-xs text-[oklch(60%_0.025_155)]">{windowTotalLabel}</span>
                      </div>
                      <HeatmapMatrix
                        columns={grid.columns}
                        valueForDate={(date) => entity.byDate.get(date) ?? 0}
                        scale={scale}
                        color={color}
                        cellSize={cellSize}
                        gap={2}
                        scrollLocked
                      />
                    </div>
                  </Reorder.Item>
                );
              })}
            </Reorder.Group>
          ) : (
            <div className="flex min-w-0 flex-col gap-3">
              {displayedEntities.map(({ entity, color }) => {
                const values = grid.allDates.map((date) => entity.byDate.get(date) ?? 0);
                const scale = intensityScale(values);
                const windowTotalLabel =
                  entity.total > 0
                    ? metric === "time"
                      ? formatDuration(entity.total, languageCode)
                      : `${entity.total}`
                    : t.common.emDash;

                return (
                  <div
                    key={entity.key}
                    className="min-w-0 overflow-hidden rounded-xl bg-[oklch(16%_0.015_165)] px-3 py-4"
                    onClick={() => openDetail(entity, color, referenceDate)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="mb-3 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2">
                        <AppIcon
                          displayName={entity.displayName}
                          appIdentifier={entity.appIdentifier}
                          category={entity.category}
                          color={color}
                          size={HABITS_APP_ICON_SIZE}
                          iconSize={HABITS_APP_ICON_RENDER_SIZE}
                          isCategory={entity.isCategory}
                        />
                        <span className="truncate text-sm font-semibold text-[oklch(90%_0.03_155)]">
                          <HabitEntityLabel entity={entity} />
                        </span>
                      </div>
                      <span className="shrink-0 text-xs text-[oklch(60%_0.025_155)]">{windowTotalLabel}</span>
                    </div>
                    <HeatmapMatrix
                      columns={grid.columns}
                      valueForDate={(date) => entity.byDate.get(date) ?? 0}
                      scale={scale}
                      color={color}
                      cellSize={cellSize}
                      gap={2}
                      scrollResetKey={scrollResetKey}
                      onCellClick={(date) => openDetail(entity, color, date)}
                    />
                  </div>
                );
              })}
            </div>
          )
        ) : null}
      </TabPageShell>

      <AppFilterSheet
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        anonymousDeviceId={anonymousDeviceId}
        heatmapStartDate={grid.startDate}
        heatmapEndDate={grid.endDate}
        entities={filterEntities}
        tracked={tracked}
        onChange={onTrackedChange}
        includeOther={includeOther}
        onIncludeOtherChange={onIncludeOtherChange}
      />

      <AppDetailDrawer
        open={detail !== null}
        onClose={() => setDetail(null)}
        appIdentifier={detail?.entity.appIdentifier}
        displayName={detail?.entity.displayName ?? ""}
        category={detail?.entity.category}
        isCategory={detail?.entity.isCategory}
        color={detail?.color ?? DEFAULT_COLOR}
        initialDate={detailDate}
        calendarScrollMode="vertical"
        habitsMetric={metric}
      />
    </>
  );
}

function PlaceholderCard({
  columns,
  cellSize,
}: {
  columns: ReturnType<typeof buildWeekGrid>["columns"];
  cellSize: number;
}) {
  return (
    <div className="min-w-0 overflow-hidden rounded-xl bg-[oklch(16%_0.015_165)] px-3 py-4">
      <div className="mb-3 flex items-center gap-2">
        <span className="h-3.5 w-3.5 rounded-full bg-[oklch(24%_0.015_165)]" />
        <span className="h-3.5 w-24 rounded bg-[oklch(24%_0.015_165)]" />
      </div>
      <HeatmapMatrix
        columns={columns}
        valueForDate={() => 0}
        scale={{ floor: 0, ceil: 0 }}
        color={DEFAULT_COLOR}
        cellSize={cellSize}
        gap={2}
      />
    </div>
  );
}

function HabitEntityLabel({ entity }: { entity: HeatmapEntity }) {
  const t = getTranslations(useScreen0Language().languageCode);
  if (entity.isCategory) {
    return localizedCategoryLabel(
      resolveCanonicalCategoryKey(entity.category ?? entity.displayName),
      t,
    );
  }
  if (isUnattributedHeatmapEntity(entity)) {
    return t.categories.other;
  }
  return (
    <AppFriendlyName
      displayName={entity.displayName}
      appIdentifier={entity.appIdentifier}
      category={entity.category}
    />
  );
}
